// The fixed set of reactions a reader can leave on a post.
//
// Mirrored on the server in server/constants/reactions.js — keep the keys and
// the order in sync. Polarity (which reactions the mobile app still sees as a
// like and which as a dislike) lives there, not here: the server decides it, and
// duplicating it would give us two places to disagree.

export const REACTIONS = [
  { key: "like", label: "Like", emoji: "👍" },
  { key: "heart", label: "Love", emoji: "❤️" },
  { key: "shocked", label: "Shocked", emoji: "😮" },
  { key: "laugh", label: "Funny", emoji: "😂" },
  { key: "sad", label: "Sad", emoji: "😢" },
  { key: "cry", label: "Crying", emoji: "😭" },
  { key: "angry", label: "Angry", emoji: "😡" },
  { key: "dislike", label: "Dislike", emoji: "👎" },
];

export const REACTION_BY_KEY = REACTIONS.reduce((acc, r) => {
  acc[r.key] = r;
  return acc;
}, {});

// The only reaction that counts as a dislike. Kept here so the optimistic
// update can keep the legacy `likes` / `dislikes` / `votes` fields honest
// between the click and the server's answer; the server is still the authority.
const NEGATIVE_KEY = "dislike";

const polarityOf = (key) => (key === NEGATIVE_KEY ? -1 : 1);

/**
 * Which reaction the viewer left, if any. Falls back to the numeric vote so the
 * page still works against a server that has not been redeployed yet.
 */
export const reactionOf = (post) => {
  if (!post) return null;
  if (post.userreaction) return post.userreaction;
  if (post.uservote === 1) return "like";
  if (post.uservote === -1) return "dislike";
  return null;
};

/** Every reaction on the post, counted. */
export const totalReactions = (post) =>
  Object.values(post?.reactions || {}).reduce((sum, n) => sum + n, 0);

/**
 * The `limit` most-used reactions, most first, for the summary beside the
 * button. Ties fall back to the canonical order so the row does not reshuffle
 * between renders.
 */
export const topReactions = (post, limit = 3) => {
  const counts = post?.reactions || {};
  return REACTIONS.filter((r) => counts[r.key] > 0)
    .sort((a, b) => counts[b.key] - counts[a.key])
    .slice(0, limit);
};

/**
 * The post as it will look once the server has accepted `key`, so the button can
 * answer the click immediately. Pure — returns a new object.
 *
 * Picking the reaction you already have removes it, which is what the server
 * does. The legacy fields move with it: nothing else on the page reads them, but
 * leaving them stale would mean two numbers on one screen disagreeing the moment
 * anything starts showing `likes` again.
 */
export const applyReaction = (post, key) => {
  if (!post) return post;

  const previous = reactionOf(post);
  const next = previous === key ? null : key;
  const reactions = { ...(post.reactions || {}) };

  if (previous) {
    reactions[previous] = Math.max(0, (reactions[previous] || 0) - 1);
    if (!reactions[previous]) delete reactions[previous];
  }
  if (next) {
    reactions[next] = (reactions[next] || 0) + 1;
  }

  const before = previous ? polarityOf(previous) : 0;
  const after = next ? polarityOf(next) : 0;

  return {
    ...post,
    reactions,
    userreaction: next,
    uservote: after,
    votes: (post.votes || 0) - before + after,
    likes: (post.likes || 0) - (before === 1 ? 1 : 0) + (after === 1 ? 1 : 0),
    dislikes:
      (post.dislikes || 0) - (before === -1 ? 1 : 0) + (after === -1 ? 1 : 0),
  };
};
